import * as Sentry from '@sentry/nextjs';

/**
 * 👤 USER CONTEXT
 * Set user context for all Sentry events
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  username?: string;
  tenantId?: string;
  tenantName?: string;
  role?: string;
  subscriptionPlan?: string;
  subscriptionStatus?: string;
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });

  // 📊 Business Context Tags (for filtering in dashboards)
  Sentry.setTags({
    tenant_id: user.tenantId,
    tenant_name: user.tenantName,
    user_role: user.role,
    subscription_plan: user.subscriptionPlan || 'trial',
    subscription_status: user.subscriptionStatus || 'unknown',
  });

  if (user.tenantId || user.role) {
    Sentry.setContext('tenant', {
      id: user.tenantId,
      name: user.tenantName,
      role: user.role,
    });
  }

  if (user.subscriptionPlan || user.subscriptionStatus) {
    Sentry.setContext('subscription', {
      plan: user.subscriptionPlan,
      status: user.subscriptionStatus,
    });
  }
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * 🍞 BREADCRUMBS
 * Add custom breadcrumb for debugging
 */
export function addSentryBreadcrumb(breadcrumb: {
  category: 'booking' | 'payment' | 'calendar' | 'email' | 'auth' | 'navigation' | 'api';
  message: string;
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
  data?: Record<string, unknown>;
}): void {
  Sentry.addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * 📊 PERFORMANCE MONITORING
 * Start a performance transaction
 */
export function startSentryPerformance(options: {
  name: string;
  op: 'http.request' | 'db.query' | 'stripe.checkout' | 'booking.create' | 'calendar.sync' | 'email.send' | 'webhook.process';
  description?: string;
  tags?: Record<string, string | number | boolean>;
  data?: Record<string, unknown>;
}): Sentry.Span | undefined {
  const span = Sentry.startInactiveSpan({
    name: options.name,
    op: options.op,
  });

  // Set tags
  if (span && options.tags) {
    Object.entries(options.tags).forEach(([key, value]) => {
      span.setAttribute?.(key, value);
    });
  }

  return span;
}

type SentryTransactionHandle = {
  setStatus: (status: string) => void;
  setData: (key: string, value: unknown) => void;
  finish: () => void;
};

function applySpanAttributes(
  span: { setAttribute?: (key: string, value: unknown) => void; setTag?: (key: string, value: unknown) => void },
  data?: Record<string, unknown>
): void {
  if (!span || !data) return;

  Object.entries(data).forEach(([key, value]) => {
    if (span.setAttribute) {
      span.setAttribute(key, value);
    } else if (span.setTag) {
      span.setTag(key, value);
    }
  });
}

export function startSentryTransaction(options: {
  name: string;
  op: string;
  tags?: Record<string, string | number | boolean>;
  data?: Record<string, unknown>;
}): SentryTransactionHandle | undefined {
  if (!Sentry.getClient()) return undefined;

  const anySentry = Sentry as unknown as {
    startTransaction?: (args: Record<string, unknown>) => any;
    startInactiveSpan?: (args: Record<string, unknown>) => any;
  };

  const span =
    anySentry.startTransaction?.({
      name: options.name,
      op: options.op,
      tags: options.tags,
      data: options.data,
    }) ??
    anySentry.startInactiveSpan?.({
      name: options.name,
      op: options.op,
    });

  if (!span) return undefined;

  applySpanAttributes(span, options.tags);
  applySpanAttributes(span, options.data);

  return {
    setStatus: (status: string) => {
      if (span.setStatus) {
        span.setStatus(status);
      } else if (span.setAttribute) {
        span.setAttribute('status', status);
      }
    },
    setData: (key: string, value: unknown) => {
      if (span.setData) {
        span.setData(key, value);
      } else if (span.setAttribute) {
        span.setAttribute(key, value);
      }
    },
    finish: () => {
      if (span.finish) {
        span.finish();
      } else if (span.end) {
        span.end();
      }
    },
  };
}

/**
 * Finish a performance transaction
 */
export function finishSentryPerformance(
  span: Sentry.Span | undefined,
  status?: 'ok' | 'error'
): void {
  if (span) {
    span.end();
  }
}

/**
 * Measure async operation performance
 */
export async function measurePerformance<T>(
  options: {
    name: string;
    op: 'http.request' | 'db.query' | 'stripe.checkout' | 'booking.create' | 'calendar.sync' | 'email.send' | 'webhook.process';
    tags?: Record<string, string | number | boolean>;
  },
  operation: () => Promise<T>
): Promise<T> {
  const span = startSentryPerformance(options);

  try {
    const result = await operation();
    finishSentryPerformance(span, 'ok');
    return result;
  } catch (error) {
    finishSentryPerformance(span, 'error');
    throw error;
  }
}

export function captureSentryException(
  error: unknown,
  context?: {
    level?: Sentry.SeverityLevel;
    tags?: Record<string, string | number | boolean>;
    extra?: Record<string, unknown>;
    fingerprint?: string[];
  }
): string {
  return Sentry.captureException(error, {
    level: context?.level || 'error',
    tags: context?.tags,
    extra: context?.extra,
    fingerprint: context?.fingerprint,
  });
}

export function captureSentryMessage(
  message: string,
  context?: {
    level?: Sentry.SeverityLevel;
    tags?: Record<string, string | number | boolean>;
    extra?: Record<string, unknown>;
  }
): string {
  return Sentry.captureMessage(message, {
    level: context?.level || 'info',
    tags: context?.tags,
    extra: context?.extra,
  });
}

function getKpiSampleRate(): number {
  const rate = Number(process.env.SENTRY_KPI_SAMPLE_RATE ?? 0.05);
  return Number.isFinite(rate) ? Math.max(0, Math.min(rate, 1)) : 0.05;
}

function shouldSample(rate = getKpiSampleRate()): boolean {
  return rate >= 1 ? true : Math.random() < rate;
}

function getSecuritySampleRate(): number {
  const rate = Number(process.env.SENTRY_SECURITY_SAMPLE_RATE ?? 0.1);
  return Number.isFinite(rate) ? Math.max(0, Math.min(rate, 1)) : 0.1;
}

export function trackSecurityEvent(data: {
  name: string;
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, unknown>;
  sampleRate?: number;
}): void {
  const rate = data.sampleRate ?? getSecuritySampleRate();
  if (!shouldSample(rate)) return;

  captureSentryMessage(data.name, {
    level: 'warning',
    tags: {
      security: true,
      ...data.tags,
    },
    extra: data.extra,
  });
}

export function trackKpiEvent(data: {
  name: string;
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, unknown>;
  sampleRate?: number;
}): void {
  const rate = data.sampleRate ?? getKpiSampleRate();
  if (!shouldSample(rate)) return;

  captureSentryMessage(data.name, {
    level: 'info',
    tags: {
      kpi: true,
      ...data.tags,
    },
    extra: data.extra,
  });
}

export const SentryBusinessEvents = {
  trackBookingCreated: (data: {
    booking_id: string;
    tenant_id: string;
    status: string;
  }) => {
    trackKpiEvent({
      name: 'booking_created',
      tags: {
        event_type: 'booking_created',
        status: data.status,
        tenant_id: data.tenant_id,
      },
      extra: {
        booking_id: data.booking_id,
        timestamp: new Date().toISOString(),
      },
    });
  },
  trackBookingFailure: (data: {
    tenant_id?: string;
    error: unknown;
  }) => {
    captureSentryException(data.error, {
      level: 'error',
      tags: {
        event_type: 'booking_failed',
        tenant_id: data.tenant_id,
      },
    });
  },
  trackPaymentSucceeded: (data: {
    tenant_id?: string;
    amount?: number;
    plan?: string;
  }) => {
    trackKpiEvent({
      name: 'payment_succeeded',
      tags: {
        event_type: 'payment_succeeded',
        tenant_id: data.tenant_id,
        plan: data.plan || 'unknown',
      },
      extra: {
        amount: data.amount,
        timestamp: new Date().toISOString(),
      },
    });
  },
  trackWebhookFailure: (data: {
    webhook_type: 'stripe' | 'google_calendar' | 'resend' | 'calendar_sync' | 'email_queue';
    event_type: string;
    error: unknown;
    retry_count?: number;
  }) => {
    captureSentryException(data.error, {
      level: 'error',
      tags: {
        event_type: 'webhook_failure',
        webhook_type: data.webhook_type,
        webhook_event: data.event_type,
        retry_count: data.retry_count || 0,
      },
    });
  },
  trackCalendarSyncFailure: (data: {
    tenant_id: string;
    calendar_source_id: string;
    provider: string;
    error: unknown;
  }) => {
    captureSentryException(data.error, {
      level: 'error',
      tags: {
        event_type: 'calendar_sync_failure',
        provider: data.provider,
      },
      extra: {
        tenant_id: data.tenant_id,
        calendar_source_id: data.calendar_source_id,
        timestamp: new Date().toISOString(),
      },
    });
  },
  trackPaymentFailure: (data: {
    tenant_id?: string;
    plan?: string;
    amount?: number;
    error_code?: string;
  }) => {
    captureSentryMessage('Payment Failure', {
      level: 'error',
      tags: {
        event_type: 'payment_failure',
        plan: data.plan || 'unknown',
        amount: data.amount || 0,
        error_code: data.error_code || 'unknown',
      },
      extra: {
        tenant_id: data.tenant_id,
        timestamp: new Date().toISOString(),
      },
    });
  },
  trackEmailQueueFailure: (data: {
    queue_id?: string;
    booking_id?: string;
    error: unknown;
  }) => {
    captureSentryException(data.error, {
      level: 'error',
      tags: {
        event_type: 'email_queue_failure',
      },
      extra: {
        queue_id: data.queue_id,
        booking_id: data.booking_id,
        timestamp: new Date().toISOString(),
      },
    });
  },
};
